import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/lib/auth";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import {
  Hexagon, ChevronLeft, ChevronRight, X, Maximize2, Minimize2, Printer,
  User, Activity, Target, Mic, FileText, Sparkles, Eye, Shield,
  Quote, TrendingUp, ClipboardList, Users, Award, Lock, Briefcase,
  Heart, BarChart3, MessageSquare, Megaphone, DollarSign, Trophy,
  Layers, Building, ArrowRight, Star, Check, Phone, Share2, FileDown, ExternalLink,
} from "lucide-react";
import {
  MouseParallaxStage,
  BentoTile,
  BentoHeadline,
  MetricTile,
  Asterisk,
  BentoTag,
  NumberedCard,
} from "@/components/pitch/Bento";
import { PricingGrid } from "@/components/pricing/PricingContent";

/**
 * OmniDraftLab — 19-slide premium pitch deck
 * - Cyan/blue base aesthetic (matches platform)
 * - Red accent reserved for AUTHORITY slides (5, 6, 17)
 * - Inline UI preview mocks per slide (no external images)
 * - window.print()-driven PDF export (one slide per page)
 */

// =====================================================================
// Hero now lives on the public landing page (`/`). Pitch deck opens at
// "The Problem" — slide 1 of 19.
// =====================================================================

// =====================================================================
// Slide 1 — The Problem
// (Hero now lives on the public landing page `/`. Constants kept here in
// case other slides ever need the same video; HeroSlide function removed.)
// =====================================================================
function ProblemSlide() {
  return (
    <MouseParallaxStage>
      <div className="flex items-center gap-3 mb-6">
        <BentoTag tone="rose">The problem</BentoTag>
        <span className="text-3 text-[10px] uppercase tracking-[0.22em]">01 · 12</span>
      </div>
      <BentoHeadline className="max-w-5xl" parallax={3}>
        Families are unprepared<br />
        for the <span className="text-rose-300 italic font-light">business</span> of the draft.
      </BentoHeadline>

      {/* Parent-questions strip — the 3 unknowns every family carries
          into the cycle. Sets up the rest of the slide emotionally. */}
      <div className="mt-7 flex flex-wrap items-center gap-x-3 gap-y-2 max-w-5xl">
        <span className="text-3 text-[10px] uppercase tracking-[0.22em] font-medium">The questions they bring:</span>
        {[
          "Is my son truly draftable?",
          "Which agent should we choose?",
          "What concerns do teams have?",
        ].map((q, i) => (
          <span
            key={q}
            data-testid={`problem-parent-q-${i + 1}`}
            className="inline-flex items-center gap-2 text-1 text-[12px] sm:text-[13px] italic"
          >
            <span className="h-1 w-1 rounded-full bg-rose-400/70" />
            "{q}"
          </span>
        ))}
      </div>

      <div className="mt-10 grid grid-cols-12 gap-3 sm:gap-4">
        {/* Big cream lead */}
        <BentoTile tone="cream" parallax={8} delay={0.05} className="col-span-12 lg:col-span-5 row-span-2 p-7 sm:p-8 flex flex-col justify-between min-h-[320px]">
          <div>
            <BentoTag tone="dark">Eye on evaluation</BentoTag>
            <h3 className="text-2xl sm:text-3xl font-semibold leading-tight tracking-tight mt-5">
              No insight into how teams actually grade prospects.
            </h3>
            <p className="text-2 text-sm mt-4 leading-relaxed">
              Tape gets reduced to a number. Character gets reduced to a rumor. The family is the last to know either.
            </p>
          </div>
          <div className="mt-8 flex items-end justify-between">
            <div className="font-semibold tabular-nums tracking-tighter leading-none" style={{ fontSize: "clamp(3.6rem, 8vw, 7rem)" }}>0%</div>
            <div className="text-right text-3 text-[10px] uppercase tracking-[0.2em] max-w-[140px]">Family visibility into front-office grading</div>
          </div>
        </BentoTile>

        {/* Trainer-first */}
        <BentoTile tone="dark" parallax={12} delay={0.12} className="col-span-12 sm:col-span-6 lg:col-span-4 p-6 min-h-[155px]">
          <div className="text-cyan-200 text-[10px] uppercase tracking-wider flex items-center gap-2"><Activity className="h-3 w-3" /> Trainer-first</div>
          <div className="text-1 text-lg font-semibold mt-2">Reps and lifts won't fix a soft draft narrative.</div>
        </BentoTile>

        {/* Cyan stat */}
        <BentoTile tone="cyan" parallax={16} delay={0.18} className="col-span-12 sm:col-span-6 lg:col-span-3 p-6 min-h-[155px] relative">
          <div className="text-[10px] uppercase tracking-[0.2em] font-medium">Round-1 vs Round-3</div>
          <div className="font-semibold tabular-nums leading-[0.85] tracking-tighter mt-2" style={{ fontSize: "clamp(2.2rem, 5vw, 3.4rem)" }}>+$8M–$15M</div>
          <div className="text-[11px] mt-2 leading-relaxed opacity-80">Average signing-bonus gap</div>
          <div className="absolute top-3 right-3"><Asterisk tone="dark" size={28} /></div>
        </BentoTile>

        {/* Narrative */}
        <BentoTile tone="glass" parallax={10} delay={0.24} className="col-span-12 sm:col-span-6 lg:col-span-4 p-6">
          <div className="text-rose-300 text-[10px] uppercase tracking-wider flex items-center gap-2"><Megaphone className="h-3 w-3" /> Narrative leak</div>
          <div className="text-1 text-base font-semibold mt-2 leading-snug">Media, social, scout chatter — driven by everyone except you.</div>
        </BentoTile>

        {/* Cost */}
        <BentoTile tone="glass" parallax={10} delay={0.30} className="col-span-12 sm:col-span-6 lg:col-span-3 p-6">
          <div className="text-amber-300 text-[10px] uppercase tracking-wider flex items-center gap-2"><DollarSign className="h-3 w-3" /> Costly forks</div>
          <div className="text-1 text-base font-semibold mt-2 leading-snug">Wrong agent. Wrong combine. Wrong answer = millions.</div>
        </BentoTile>

        {/* THE GAP — merged in from former slide 2.
            Compact two-column comparison: training world vs NFL reality. */}
        <BentoTile tone="dark" parallax={6} delay={0.36} className="col-span-12 p-6 sm:p-7">
          <div className="flex items-center gap-2 mb-4">
            <BentoTag tone="outline">The gap</BentoTag>
            <span className="text-2 text-sm sm:text-base font-medium">
              Everyone trains the athlete. <span className="text-cyan-300">No one prepares the outcome.</span>
            </span>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="rounded-xl bg-white/[0.03] border border-white/10 p-4">
              <div className="text-3 text-[10px] uppercase tracking-[0.2em] mb-2.5">The training world</div>
              <ul className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                {["Speed work", "Lifting numbers", "Drill mechanics", "Position camps"].map((x) => (
                  <li key={x} className="flex items-center gap-2 text-2 text-xs sm:text-sm">
                    <span className="h-4 w-4 rounded bg-white/5 grid place-items-center text-3 font-mono text-[10px]">—</span>
                    {x}
                  </li>
                ))}
              </ul>
              <div className="text-3 text-[11px] mt-3 italic">Important. But finite.</div>
            </div>
            <div className="rounded-xl border border-cyan-300/30 bg-cyan-300/[0.06] p-4">
              <div className="text-cyan-300 text-[10px] uppercase tracking-[0.2em] mb-2.5">The NFL reality</div>
              <ul className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                {["Front-office grades", "Scheme & roster fit", "Locker-room character", "Strategic decisions"].map((x) => (
                  <li key={x} className="flex items-center gap-2 text-1 text-xs sm:text-sm">
                    <span className="h-4 w-4 rounded bg-cyan-300 text-[#04111A] grid place-items-center"><Check className="h-2.5 w-2.5" strokeWidth={3} /></span>
                    {x}
                  </li>
                ))}
              </ul>
              <div className="text-cyan-200 text-[11px] mt-3 font-medium">This is where draft outcomes are won.</div>
            </div>
          </div>
        </BentoTile>
      </div>
    </MouseParallaxStage>
  );
}

// =====================================================================
// Slide 3 — The Gap
// =====================================================================
function GapSlide() {
  const left = ["Speed work", "Lifting numbers", "Drill mechanics", "Position camps"];
  const right = ["Front-office grades", "Scheme & roster fit", "Locker-room character", "Strategic decisions"];
  return (
    <div>
      <Eyebrow>The gap</Eyebrow>
      <h2 className="text-3xl sm:text-5xl font-semibold tracking-tight text-1 leading-tight max-w-4xl">
        Everyone trains the athlete. <span className="text-cyan-300">No one prepares the outcome.</span>
      </h2>
      <div className="mt-10 grid lg:grid-cols-2 gap-4">
        <div className="glass p-6">
          <div className="text-3 text-[10px] uppercase tracking-[0.2em] mb-3">The training world</div>
          <ul className="space-y-2.5">
            {left.map((x) => (
              <li key={x} className="flex items-center gap-3 text-2 text-sm">
                <span className="h-6 w-6 rounded-md bg-white/5 grid place-items-center text-3 font-mono text-[11px]">—</span>
                {x}
              </li>
            ))}
          </ul>
          <div className="text-3 text-xs mt-5 italic">Important. But finite.</div>
        </div>
        <div className="rounded-2xl border border-cyan-300/30 bg-cyan-300/[0.05] p-6">
          <div className="text-cyan-300 text-[10px] uppercase tracking-[0.2em] mb-3">The NFL reality</div>
          <ul className="space-y-2.5">
            {right.map((x) => (
              <li key={x} className="flex items-center gap-3 text-1 text-sm">
                <span className="h-6 w-6 rounded-md bg-cyan-300 text-[#04111A] grid place-items-center"><Check className="h-3.5 w-3.5" strokeWidth={3} /></span>
                {x}
              </li>
            ))}
          </ul>
          <div className="text-cyan-200 text-xs mt-5 font-medium">This is where draft outcomes are won.</div>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// Slide 2 — The Solution (positioning statement)
// Rebuilt 2026-02-08: removed "Pillar 01-04" + "Six branches, one athlete"
// which duplicated the 4-Layer framework on Slide 5. This slide is now
// purely the positioning manifesto; the 4-Layer system is revealed on
// Slide 5 only — one framework, one name, used everywhere downstream.
// =====================================================================
function SolutionSlide() {
  return (
    <MouseParallaxStage>
      <div className="flex items-center gap-3 mb-6">
        <BentoTag tone="cyan">The solution</BentoTag>
        <span className="text-3 text-[10px] uppercase tracking-[0.22em]">02 · 12</span>
      </div>

      <BentoHeadline className="max-w-[1180px]" parallax={3}>
        We do not <span className="text-rose-400 italic font-light">train</span> athletes.<br />
        We <span className="bg-gradient-to-r from-cyan-300 to-blue-400 bg-clip-text text-transparent italic font-light">prepare</span> them for how the NFL actually works.
      </BentoHeadline>

      <div className="mt-7 grid grid-cols-12 gap-3 sm:gap-4">
        {/* Big positioning statement — cream lead, takes the eye */}
        <BentoTile tone="cream" parallax={10} delay={0.05} className="col-span-12 lg:col-span-8 row-span-2 p-7 sm:p-9 flex flex-col justify-between min-h-[320px] relative">
          <div>
            <BentoTag tone="dark">Primary positioning</BentoTag>
            <p className="text-2xl sm:text-[1.75rem] font-semibold tracking-tight leading-[1.15] mt-6 max-w-[44ch]">
              We help athletes and families navigate one of the <span className="italic font-light">most important and expensive</span> decisions of their lives —
            </p>
            <p className="text-2xl sm:text-[1.75rem] font-semibold tracking-tight leading-[1.15] mt-3 max-w-[44ch]">
              using the same <span className="italic font-light">evaluation, intelligence gathering, and strategic planning</span> processes used inside NFL organizations.
            </p>
          </div>
          <div className="mt-7 flex items-end justify-between gap-4">
            <p className="text-2 text-sm italic leading-snug max-w-md">
              Not coaching. Not training. <span className="not-italic font-semibold text-black">Intelligence.</span> The part the families never had access to until now.
            </p>
            <ClipboardList className="h-9 w-9 opacity-60 shrink-0" strokeWidth={1.4} />
          </div>
          <div className="absolute top-6 right-6"><Asterisk tone="rose" size={36} /></div>
        </BentoTile>

        {/* What we ARE — cyan */}
        <BentoTile tone="cyan" parallax={14} delay={0.12} className="col-span-12 sm:col-span-6 lg:col-span-4 p-6 min-h-[155px]">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-medium">
            <Check className="h-3.5 w-3.5" strokeWidth={3} /> What we are
          </div>
          <div className="text-lg font-semibold mt-3 leading-snug">
            A pre-draft intelligence, strategy, and positioning operating system.
          </div>
          <div className="text-[11px] mt-2 opacity-80 italic">Built for athletes and families who refuse to leave the most expensive decision of their lives to chance.</div>
        </BentoTile>

        {/* What we are NOT — dark */}
        <BentoTile tone="dark" parallax={14} delay={0.18} className="col-span-12 sm:col-span-6 lg:col-span-4 p-6 min-h-[155px]">
          <div className="flex items-center gap-2 text-rose-300 text-[10px] uppercase tracking-[0.22em] font-medium">
            <X className="h-3.5 w-3.5" strokeWidth={3} /> What we are not
          </div>
          <div className="text-1 text-lg font-semibold mt-3 leading-snug">
            Coaches. Trainers. Recruiters. Hype men.
          </div>
          <div className="text-3 text-[11px] mt-2 italic">There's no shortage of those. There is one of us.</div>
        </BentoTile>
      </div>
    </MouseParallaxStage>
  );
}

// =====================================================================
// Slide 5 — Authority (RED ACCENT)
// =====================================================================
function AuthoritySlide() {
  return (
    <MouseParallaxStage>
      <div className="flex items-center gap-3 mb-6">
        <BentoTag tone="rose">Your authority</BentoTag>
        <span className="text-3 text-[10px] uppercase tracking-[0.22em]">03 · 12</span>
      </div>
      <BentoHeadline className="max-w-5xl" parallax={3}>
        Built from <span className="text-rose-400 italic font-light">inside</span><br />
        the league.
      </BentoHeadline>

      <div className="mt-9 grid grid-cols-12 gap-3 sm:gap-4">
        {/* Founder card — rose tile with Brandon's headshot */}
        <BentoTile tone="rose" parallax={10} delay={0.05} className="col-span-12 lg:col-span-5 row-span-2 p-7 sm:p-8 flex flex-col justify-between min-h-[380px] relative">
          <div>
            <BentoTag tone="dark">Founder</BentoTag>
            <div className="mt-7 h-56 w-56 sm:h-64 sm:w-64 rounded-2xl overflow-hidden ring-2 ring-white/30 shadow-[0_14px_36px_-12px_rgba(0,0,0,0.55)]">
              <img
                src="https://customer-assets.emergentagent.com/job_d0eec6c2-63ee-438c-aaf1-8fb267ea6d12/artifacts/hs97j4ix_1545437839588.jpeg"
                alt="Brandon A. Taylor"
                className="h-full w-full object-cover object-top"
                data-testid="authority-founder-photo"
              />
            </div>
            <h3 className="text-white text-4xl sm:text-5xl font-semibold tracking-tight mt-6 leading-[1.05]">Brandon A. Taylor</h3>
            <div className="text-white/85 text-[12px] uppercase tracking-[0.22em] mt-3 font-medium">Former NFL Personnel Executive</div>
          </div>
          <p className="text-white text-base mt-7 italic leading-snug">"I know how decisions are made — because I helped make them."</p>
          <div className="absolute top-5 right-5"><Asterisk tone="dark" size={32} /></div>
        </BentoTile>

        {/* 20 years — cream */}
        <MetricTile
          tone="cream"
          eyebrow="NFL + AFL Personnel"
          value="20"
          label="Years inside the league"
          sub="Three franchises · countless draft rooms"
          parallax={14}
          delay={0.12}
          className="col-span-12 sm:col-span-6 lg:col-span-4 min-h-[155px]"
          testId="authority-metric-years"
        />

        {/* 3 franchises — dark, with Panthers + Titans + Destroyers logos */}
        <BentoTile
          tone="dark"
          parallax={14}
          delay={0.18}
          className="col-span-12 sm:col-span-6 lg:col-span-3 p-5 sm:p-6 flex flex-col justify-between min-h-[155px] relative"
          testId="authority-metric-franchises"
        >
          <div className="text-[10px] uppercase tracking-[0.22em] font-medium opacity-80">Franchises</div>
          <div className="flex items-center justify-center gap-3 my-3 flex-1">
            <img
              src="https://customer-assets.emergentagent.com/job_33501231-3bb8-453d-9304-4d494d4f9c77/artifacts/rphrbf93_Carolina_Panthers_logo.svg.png"
              alt="Carolina Panthers"
              className="h-10 sm:h-12 w-auto object-contain drop-shadow-[0_4px_12px_rgba(0,133,202,0.35)]"
              data-testid="authority-logo-panthers"
            />
            <img
              src="https://customer-assets.emergentagent.com/job_33501231-3bb8-453d-9304-4d494d4f9c77/artifacts/ezghvr1w_Tennessee_Titans_Logo_2026.svg.png"
              alt="Tennessee Titans"
              className="h-10 sm:h-12 w-auto object-contain drop-shadow-[0_4px_12px_rgba(75,146,219,0.35)]"
              data-testid="authority-logo-titans"
            />
            <img
              src="https://customer-assets.emergentagent.com/job_d0eec6c2-63ee-438c-aaf1-8fb267ea6d12/artifacts/nlaoyrre_Columbus_Destroyers_logo.png"
              alt="Columbus Destroyers"
              className="h-10 sm:h-12 w-auto object-contain drop-shadow-[0_4px_12px_rgba(255,255,255,0.18)] bg-white/95 rounded p-0.5"
              data-testid="authority-logo-destroyers"
            />
          </div>
          <div className="text-[10px] leading-snug opacity-75 text-center">Carolina · Tennessee · Columbus</div>
        </BentoTile>

        {/* ∞ — cyan */}
        <BentoTile tone="cyan" parallax={10} delay={0.24} className="col-span-12 sm:col-span-6 lg:col-span-4 p-6 sm:p-7 flex flex-col justify-between min-h-[150px] relative">
          <div className="text-[10px] uppercase tracking-[0.22em] font-medium">Draft cycles</div>
          <div className="font-semibold tabular-nums leading-[0.85] tracking-tighter mt-3" style={{ fontSize: "clamp(3.5rem, 8vw, 6rem)" }}>∞</div>
          <div className="text-[11px] mt-2 leading-relaxed opacity-80">Scouting cycles · roster builds · trade rooms</div>
          <div className="absolute top-3 right-3"><Asterisk tone="dark" size={26} /></div>
        </BentoTile>

        {/* Quote glass */}
        <BentoTile tone="glass" parallax={6} delay={0.30} className="col-span-12 lg:col-span-3 p-6 flex flex-col justify-center min-h-[120px]">
          <Quote className="h-5 w-5 text-rose-300" />
          <div className="text-1 text-sm mt-3 leading-relaxed italic">Authority is built in the room — not in the rep.</div>
        </BentoTile>
      </div>
    </MouseParallaxStage>
  );
}

// =====================================================================
// Slide 6 — What We Do (RED ACCENT)
// =====================================================================
// =====================================================================
// Slide 6 — The Promise
// =====================================================================
function PromiseSlide() {
  return (
    <MouseParallaxStage>
      <div className="flex items-center gap-3 mb-6">
        <BentoTag tone="rose">The guarantee</BentoTag>
        <span className="text-3 text-[10px] uppercase tracking-[0.22em]">04 · 12</span>
      </div>
      <BentoHeadline className="max-w-5xl" parallax={3}>
        We are not selling <span className="text-rose-400 italic font-light">hope.</span><br />
        We are providing <span className="text-cyan-300 italic font-light">clarity.</span>
      </BentoHeadline>

      <div className="mt-9 grid grid-cols-12 gap-3 sm:gap-4">
        {/* What we do NOT guarantee — cream, brutal honesty */}
        <BentoTile tone="cream" parallax={10} delay={0.05} className="col-span-12 lg:col-span-6 p-7 sm:p-9 flex flex-col justify-between min-h-[320px] relative">
          <div>
            <BentoTag tone="dark">What we do not guarantee</BentoTag>
            <h3 className="text-2xl sm:text-[1.85rem] font-semibold tracking-tight mt-6 leading-[1.1]">
              Outcomes belong to <span className="italic font-light">32 front offices.</span>
            </h3>
            <ul className="mt-6 space-y-2.5">
              {[
                "Draft position",
                "Draft round",
                "An NFL contract",
                "NFL success",
              ].map((f) => (
                <li key={f} className="text-2 text-[14px] flex items-center gap-3">
                  <span className="h-5 w-5 rounded-full bg-rose-500/15 ring-1 ring-rose-400/40 grid place-items-center shrink-0">
                    <X className="h-3 w-3 text-rose-600" strokeWidth={3} />
                  </span>
                  <span className="font-medium">{f}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-7 text-3 text-[11px] italic leading-relaxed max-w-md">
            Anyone in football who tells you otherwise isn't in football.
          </div>
          <div className="absolute top-6 right-6"><Asterisk tone="rose" size={36} /></div>
        </BentoTile>

        {/* What we DO guarantee — cyan, the affirmation */}
        <BentoTile tone="cyan" parallax={14} delay={0.12} className="col-span-12 lg:col-span-6 p-7 sm:p-9 flex flex-col justify-between min-h-[320px] relative">
          <div>
            <BentoTag tone="dark">What we do guarantee</BentoTag>
            <h3 className="text-2xl sm:text-[1.85rem] font-semibold tracking-tight mt-6 leading-[1.1]">
              Every athlete and family enters the process with the <span className="italic font-light">highest level</span> of preparation, intelligence, and strategic guidance available.
            </h3>
          </div>
          <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2">
            {[
              "NFL-grade evaluation",
              "Front-office intelligence",
              "Interview readiness",
              "Decision support, every fork",
              "Strategic positioning",
              "A family that's never the last to know",
            ].map((f) => (
              <li key={f} className="text-[13px] flex items-start gap-2 leading-snug">
                <Check className="h-3.5 w-3.5 mt-1 shrink-0" strokeWidth={3} />
                <span className="font-medium">{f}</span>
              </li>
            ))}
          </ul>
          <div className="absolute top-6 right-6"><Asterisk tone="dark" size={36} /></div>
        </BentoTile>
      </div>

      <div className="mt-8 text-3 text-[11px] uppercase tracking-[0.22em] italic">
        Outcomes belong to teams. Readiness — across all four layers — belongs to us.
      </div>
    </MouseParallaxStage>
  );
}

function WhatWeDoSlide() {
  // --- "How we work" — the 4-Layer framework slide. -------------------
  // The deck used to show 5 verbs here; the 4-Layer thesis is sharper.
  // Each tile = one layer + 3 proof points + a single lucide icon.
  const LAYERS = [
    {
      n: "01",
      tone: "cyan",
      title: "Evaluation",
      icon: Activity,
      lede: "How the league actually grades him.",
      bullets: [
        "Scouting reports + 1.0–9.0 trait analysis",
        "Team-fit board (8 sub-scores, ceiling/floor)",
        "Draft projection w/ confidence + AI read",
      ],
    },
    {
      n: "02",
      tone: "glass",
      title: "Preparation",
      icon: Mic,
      lede: "Ready for every room he'll walk into.",
      bullets: [
        "AI interview prep — 6-dim scoring",
        "Whiteboard, install + media drills",
        "Psychological readiness & presence",
      ],
    },
    {
      n: "03",
      tone: "rose",
      title: "Strategy",
      icon: Target,
      lede: "Position him exactly where he belongs.",
      bullets: [
        "Agent + team comms guidance",
        "Combine / Pro-Day playbook",
        "NIL & decision-filter framework",
      ],
    },
    {
      n: "04",
      tone: "cream",
      title: "Family",
      icon: Heart,
      lede: "Never the last to know.",
      bullets: [
        "Branded parent portal + calendar",
        "Education, expectations, transparency",
        "Direct comms with the advisory team",
      ],
    },
  ];

  return (
    <MouseParallaxStage>
      <div className="flex items-center gap-3 mb-6">
        <BentoTag tone="rose">How we work</BentoTag>
        <span className="text-3 text-[10px] uppercase tracking-[0.22em]">05 · 12</span>
      </div>
      <h2
        className="font-semibold tracking-tight text-1 leading-[1.05] max-w-5xl"
        style={{ fontSize: "clamp(2rem, 4.4vw, 3.8rem)" }}
      >
        Four layers. One athlete. <span className="text-cyan-300 italic font-light">Every angle covered.</span>
      </h2>
      <p className="text-2 text-sm sm:text-base mt-4 max-w-2xl leading-relaxed">
        OmniDraftLab is built on a four-layer system. Every feature in this deck plugs into one of them.
      </p>

      {/* "What parents are really buying" tagline strip — anchors the
          4-Layer system back to the human outcomes the family pays for. */}
      <div className="mt-5 flex flex-wrap items-center gap-x-2 gap-y-2 max-w-3xl">
        <span className="text-3 text-[10px] uppercase tracking-[0.22em] font-medium">What parents are really buying:</span>
        {[
          { word: "Confidence", tone: "cyan" },
          { word: "Clarity",    tone: "white" },
          { word: "Truth",      tone: "rose" },
          { word: "Guidance",   tone: "white" },
          { word: "Access",     tone: "cyan" },
        ].map(({ word, tone }, i, arr) => (
          <span key={word} className="inline-flex items-center gap-2" data-testid={`layers-buying-pill-${i + 1}`}>
            <span
              className={`text-[11px] sm:text-[12px] font-semibold tracking-tight ${
                tone === "cyan" ? "text-cyan-300" :
                tone === "rose" ? "text-rose-300" :
                "text-1"
              }`}
            >
              {word}
            </span>
            {i < arr.length - 1 && <span className="text-3 text-[10px]">·</span>}
          </span>
        ))}
      </div>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
        {LAYERS.map((L, i) => {
          const Icon = L.icon;
          // Brighter, higher-contrast titles on the saturated cyan/rose tiles.
          // 2026-02-08 fix: previously cyan-200 on cyan / rose-300 on rose was
          // visually muted; switched to pure white for an editorial pop.
          const accent =
            L.tone === "cyan" ? "text-white" :
            L.tone === "rose" ? "text-white" :
            L.tone === "cream" ? "text-[#0E1116]" : "text-1";
          const numberColor =
            L.tone === "cyan" ? "rgba(255,255,255,0.85)" :
            L.tone === "rose" ? "rgba(255,255,255,0.85)" :
            L.tone === "cream" ? "rgba(14,17,22,0.4)" : "rgba(255,255,255,0.4)";
          return (
            <BentoTile
              key={L.n}
              tone={L.tone}
              parallax={9 + i * 1.5}
              delay={0.05 + i * 0.07}
              className="p-6 sm:p-8 min-h-[230px] flex flex-col justify-between relative"
              testId={`fourlayers-tile-${L.title.toLowerCase()}`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className={`text-[10px] uppercase tracking-[0.22em] font-medium ${L.tone === "cream" ? "opacity-70" : "opacity-80"}`}>
                    Layer {L.n}
                  </div>
                  <div className={`mt-2 flex items-center gap-2.5 ${accent}`}>
                    <Icon className="h-5 w-5" strokeWidth={1.8} />
                    <h3 className="text-2xl sm:text-3xl font-semibold tracking-tight leading-none">
                      {L.title}
                    </h3>
                  </div>
                  <p className={`mt-3 text-[13px] sm:text-sm leading-snug max-w-[28ch] ${L.tone === "cream" ? "opacity-75" : "text-2"}`}>
                    {L.lede}
                  </p>
                </div>
                <span
                  aria-hidden="true"
                  className="font-semibold tabular-nums tracking-tighter leading-none select-none"
                  style={{
                    fontSize: "clamp(2.6rem, 4vw, 3.6rem)",
                    WebkitTextStroke: `1.5px ${numberColor}`,
                    color: "transparent",
                  }}
                >
                  {L.n}
                </span>
              </div>

              <ul className={`mt-6 space-y-1.5 ${L.tone === "cream" ? "" : "text-2"}`}>
                {L.bullets.map((b) => (
                  <li key={b} className="text-[12.5px] sm:text-sm flex items-start gap-2 leading-snug">
                    <Check className={`h-3.5 w-3.5 mt-1 shrink-0 ${L.tone === "cream" ? "" : "opacity-80"}`} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </BentoTile>
          );
        })}
      </div>

      <div className="mt-7 text-3 text-[11px] uppercase tracking-[0.22em] italic">
        Outcomes belong to teams. Readiness — across all four layers — belongs to us.
      </div>
    </MouseParallaxStage>
  );
}

// =====================================================================
// Slide 7 — Player Intelligence Report (BENTO)
// =====================================================================
function IntelReportSlide() {
  return (
    <div className="relative">
      <LayerBadge n={1} />
      <Eyebrow>Core product</Eyebrow>
      <h2 className="text-3xl sm:text-5xl font-semibold tracking-tight text-1 leading-tight max-w-4xl">
        The Player <span className="text-cyan-300">Intelligence Report.</span>
      </h2>
      <p className="text-2 text-base mt-3 max-w-2xl">Eight evaluation lenses · one document · how the league sees him.</p>
      <div className="mt-8 grid grid-cols-12 gap-3">
        {/* Big tile: Grade */}
        <div className="col-span-12 sm:col-span-5 rounded-2xl border border-white/10 bg-white/[0.04] p-6">
          <div className="text-3 text-[10px] uppercase tracking-wider">NFL Grade · 1.0–9.0</div>
          <div className="flex items-end gap-3 mt-2">
            <div className="text-cyan-300 text-7xl font-semibold tabular-nums leading-none">7.4</div>
            <div className="pb-2">
              <div className="text-1 text-sm">Day 2 lock</div>
              <div className="text-3 text-xs">Round 2–3 range</div>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-9 gap-1">
            {[1,2,3,4,5,6,7,8,9].map((n) => (
              <div key={n} className={`h-7 rounded-md text-[10px] grid place-items-center font-mono ${n <= 7 ? "bg-cyan-300 text-[#04111A]" : "bg-white/5 text-3"}`}>{n}</div>
            ))}
          </div>
        </div>
        {/* Strengths */}
        <div className="col-span-12 sm:col-span-4 glass p-5">
          <div className="text-emerald-300 text-[10px] uppercase tracking-wider">Strengths</div>
          <ul className="mt-2 space-y-1.5 text-2 text-sm">
            <li>• Layered touch on intermediate routes</li>
            <li>• Pre-snap diagnosis &amp; protection IDs</li>
            <li>• Off-platform throws to the boundary</li>
          </ul>
        </div>
        {/* Weaknesses */}
        <div className="col-span-12 sm:col-span-3 glass p-5">
          <div className="text-rose-300 text-[10px] uppercase tracking-wider">Weaknesses</div>
          <ul className="mt-2 space-y-1.5 text-2 text-sm">
            <li>• Footwork under interior heat</li>
            <li>• Anticipation vs. zone</li>
          </ul>
        </div>
        {/* Scheme fit */}
        <div className="col-span-6 sm:col-span-3 glass p-5">
          <div className="text-3 text-[10px] uppercase tracking-wider">Scheme fit</div>
          <div className="text-1 text-sm font-semibold mt-1">West Coast / Shanahan tree</div>
        </div>
        {/* Comp */}
        <div className="col-span-6 sm:col-span-3 glass p-5">
          <div className="text-3 text-[10px] uppercase tracking-wider">Player comp</div>
          <div className="text-1 text-sm font-semibold mt-1">Kirk Cousins (entry)</div>
        </div>
        {/* Projection */}
        <div className="col-span-6 sm:col-span-3 glass p-5">
          <div className="text-3 text-[10px] uppercase tracking-wider">Projection</div>
          <div className="text-1 text-sm font-semibold mt-1">Floor R3 · Ceiling R1</div>
        </div>
        {/* How the league sees him */}
        <div className="col-span-6 sm:col-span-3 rounded-2xl border border-cyan-300/30 bg-cyan-300/[0.06] p-5">
          <div className="text-cyan-200 text-[10px] uppercase tracking-wider">League view</div>
          <div className="text-1 text-sm font-semibold mt-1">Spot-starter w/ trajectory</div>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// Slide 8 — Draft Strategy Layer
// =====================================================================
function DraftStrategySlide() {
  return (
    <div className="relative grid lg:grid-cols-12 gap-10 items-center">
      <LayerBadge n={3} />
      <div className="lg:col-span-7">
        <Eyebrow>Draft strategy</Eyebrow>
        <h2 className="text-3xl sm:text-5xl font-semibold tracking-tight text-1 leading-tight">
          We don't <span className="text-3 line-through">guess</span> the draft. We <span className="text-cyan-300">model</span> it.
        </h2>
        <ul className="mt-7 space-y-2.5">
          {[
            "Range projection (R1 / R2 / R3 with confidence)",
            "32-team fit matrix · scheme + roster opportunity",
            "Combine & Pro Day strategy (test or sit, by drill)",
            "Narrative shaping for media, scouts, and locker rooms",
          ].map((b, i) => (
            <motion.li key={i} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.08 + i * 0.06 }} className="flex items-start gap-3 text-2">
              <span className="mt-2 h-1.5 w-1.5 rounded-full bg-cyan-300 shrink-0" />
              <span className="text-sm sm:text-base">{b}</span>
            </motion.li>
          ))}
        </ul>
      </div>
      {/* Mock team-fit grid */}
      <div className="lg:col-span-5">
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
          <div className="text-3 text-[10px] uppercase tracking-wider mb-3">Top-tier fits</div>
          <div className="space-y-2">
            {[
              { abbr: "DEN", name: "Denver Broncos", score: 92 },
              { abbr: "ATL", name: "Atlanta Falcons", score: 88 },
              { abbr: "PIT", name: "Pittsburgh Steelers", score: 84 },
              { abbr: "MIN", name: "Minnesota Vikings", score: 81 },
            ].map((t) => (
              <div key={t.abbr} className="flex items-center gap-3 p-2.5 rounded-lg bg-white/[0.03]">
                <div className="h-9 w-9 rounded-md bg-gradient-to-br from-cyan-400 to-blue-500 text-[#04111A] grid place-items-center text-xs font-bold">{t.abbr}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-1 text-sm">{t.name}</div>
                  <div className="h-1.5 mt-1 bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-cyan-400 to-blue-500" style={{ width: `${t.score}%` }} />
                  </div>
                </div>
                <div className="text-cyan-300 font-mono font-semibold tabular-nums">{t.score}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// Slide 9 — Team Blueprint (Org Chart)
// =====================================================================
function TeamBlueprintSlide() {
  const branches = [
    { n: "01", label: "Scouting & Intelligence", icon: ClipboardList, subs: ["Tape evaluation", "Source intel", "League view"], tone: "cyan", span: "lg:col-span-5" },
    { n: "02", label: "Performance Team", icon: Activity, subs: ["Position coach", "S&C", "Speed"], tone: "dark", span: "lg:col-span-3" },
    { n: "03", label: "Mental & Interview", icon: Mic, subs: ["Combine prep", "Whiteboard", "Comms"], tone: "glass", span: "lg:col-span-4" },
    { n: "04", label: "Business & Representation", icon: Briefcase, subs: ["Agent vetting", "Financial plan", "Contracts"], tone: "glass", span: "lg:col-span-4" },
    { n: "05", label: "Brand & Media", icon: Megaphone, subs: ["NIL", "Press training", "Narrative"], tone: "dark", span: "lg:col-span-4" },
    { n: "06", label: "Family Advisory", icon: Heart, subs: ["Decisions", "Expectations", "Comms"], tone: "cream", span: "lg:col-span-4" },
  ];

  const toneCard = {
    cyan: "rounded-2xl border border-cyan-300/30 bg-cyan-300/[0.07] backdrop-blur-md",
    dark: "rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-md",
    glass: "rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-md",
    cream: "bento-cream",
  };
  const toneLabel = { cyan: "text-cyan-200", dark: "text-1", glass: "text-1", cream: "text-1" };
  const toneIcon = { cyan: "text-cyan-200", dark: "text-cyan-300", glass: "text-cyan-300", cream: "text-rose-500" };
  const toneSub = { cyan: "text-cyan-200/70", dark: "text-3", glass: "text-3", cream: "text-2" };

  return (
    <MouseParallaxStage>
      <LayerBadge n={3} />
      <div className="flex items-center gap-3 mb-6">
        <BentoTag tone="cyan">The team blueprint</BentoTag>
        <span className="text-3 text-[10px] uppercase tracking-[0.22em]">09 · 19</span>
      </div>
      <h2 className="font-semibold tracking-tight text-1 leading-[1.05] max-w-4xl" style={{ fontSize: "clamp(2rem, 4.2vw, 3.6rem)" }}>
        A <span className="text-cyan-300 italic font-light">six-branch</span> organization built around the athlete.
      </h2>

      <div className="mt-7 flex justify-center">
        <div className="px-5 py-2.5 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-500 text-[#04111A] font-semibold inline-flex items-center gap-2 text-sm shadow-[0_18px_40px_-12px_rgba(34,211,238,0.55)]">
          <Trophy className="h-4 w-4" /> YOU · GM &amp; Strategy
        </div>
      </div>

      <div className="mt-7 grid grid-cols-12 gap-3 sm:gap-4">
        {branches.map((b, i) => {
          const Icon = b.icon;
          return (
            <motion.div
              key={b.label}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 + i * 0.05, duration: 0.55, ease: [0.22, 0.65, 0.2, 1] }}
              className={`col-span-12 sm:col-span-6 ${b.span} ${toneCard[b.tone]} p-5 sm:p-6 relative flex flex-col`}
              data-testid={`blueprint-card-${i + 1}`}
            >
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.22em] font-medium opacity-60">{b.n}</div>
                <Icon className={`h-5 w-5 ${toneIcon[b.tone]}`} strokeWidth={1.6} />
              </div>
              <div className={`mt-4 font-semibold tracking-tight leading-[1.1] ${toneLabel[b.tone]}`} style={{ fontSize: "clamp(1.05rem, 1.4vw, 1.35rem)" }}>
                {b.label}
              </div>
              <ul className="mt-3 flex flex-wrap gap-x-3 gap-y-1">
                {b.subs.map((s) => (
                  <li key={s} className={`text-[11px] flex items-center gap-1.5 ${toneSub[b.tone]}`}>
                    <span className="h-1 w-1 rounded-full bg-current opacity-60" /> {s}
                  </li>
                ))}
              </ul>
            </motion.div>
          );
        })}
      </div>
    </MouseParallaxStage>
  );
}

// =====================================================================
// Slide 10 — Performance Team
// =====================================================================
function PerformanceSlide() {
  const roles = [
    { icon: ClipboardList, t: "Position Coach", d: "On-tape technique, system mastery" },
    { icon: Activity, t: "Strength & Conditioning", d: "Combine + camp readiness" },
    { icon: TrendingUp, t: "Speed Specialist", d: "10-yd, change-of-direction, drills" },
    { icon: Heart, t: "Sports Medicine", d: "Pre-hab, recovery, medical narrative" },
  ];
  return (
    <div className="relative">
      <LayerBadge n={2} />
      <Eyebrow>Performance team</Eyebrow>
      <h2 className="text-3xl sm:text-5xl font-semibold tracking-tight text-1 leading-tight max-w-4xl">
        We train what shows up <span className="text-cyan-300">on tape.</span>
      </h2>
      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {roles.map((r, i) => {
          const Icon = r.icon;
          return (
            <motion.div key={r.t} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 + i * 0.06 }} className="glass p-5">
              <div className="h-9 w-9 rounded-lg bg-cyan-300/15 text-cyan-300 grid place-items-center"><Icon className="h-4 w-4" /></div>
              <div className="text-1 text-sm font-semibold mt-3">{r.t}</div>
              <div className="text-3 text-xs mt-1">{r.d}</div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

// =====================================================================
// Slide 11 — Mental & Interview Prep
// =====================================================================
function InterviewPrepSlide() {
  const dims = ["Clarity", "Confidence", "Authenticity", "Accountability", "Football IQ", "Presence"];
  return (
    <div className="relative grid lg:grid-cols-12 gap-10 items-center">
      <LayerBadge n={2} />
      <div className="lg:col-span-7">
        <Eyebrow>Mental &amp; Interview Prep</Eyebrow>
        <h2 className="text-3xl sm:text-5xl font-semibold tracking-tight text-1 leading-tight">
          Most players fail here <span className="text-rose-300">quietly.</span>
        </h2>
        <ul className="mt-7 space-y-2.5 text-2 text-base">
          <li className="flex items-center gap-3"><span className="h-1.5 w-1.5 rounded-full bg-cyan-300" />Combine interviews — formal &amp; informal</li>
          <li className="flex items-center gap-3"><span className="h-1.5 w-1.5 rounded-full bg-cyan-300" />Whiteboard sessions — scheme &amp; protections</li>
          <li className="flex items-center gap-3"><span className="h-1.5 w-1.5 rounded-full bg-cyan-300" />Media &amp; team-meeting communication</li>
        </ul>
      </div>
      <div className="lg:col-span-5">
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
          <div className="text-3 text-[10px] uppercase tracking-wider mb-3">Six-dimension scoring</div>
          <div className="space-y-2.5">
            {dims.map((d, i) => {
              const v = [82, 76, 88, 70, 79, 84][i];
              return (
                <div key={d} className="flex items-center gap-3">
                  <div className="text-2 text-xs w-28 shrink-0">{d}</div>
                  <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-cyan-400 to-blue-500" style={{ width: `${v}%` }} />
                  </div>
                  <div className="text-cyan-300 font-mono text-xs tabular-nums w-8 text-right">{v}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// Slide 12 — Business & Brand
// =====================================================================
function BusinessBrandSlide() {
  const tiles = [
    { icon: Briefcase, t: "Agent Selection", d: "We guide. You decide." },
    { icon: DollarSign, t: "Financial Planning", d: "Pre-draft signing-bonus modeling" },
    { icon: Star, t: "NIL & Brand", d: "Position him as a portfolio, not a logo" },
    { icon: MessageSquare, t: "Media Training", d: "Sound-bite ready, scout-room aware" },
  ];
  return (
    <div className="relative">
      <LayerBadge n={3} />
      <Eyebrow>Business &amp; Brand</Eyebrow>
      <h2 className="text-3xl sm:text-5xl font-semibold tracking-tight text-1 leading-tight max-w-4xl">
        The career is the <span className="text-cyan-300">long game.</span> We build it now.
      </h2>
      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {tiles.map((x, i) => {
          const Icon = x.icon;
          return (
            <motion.div key={x.t} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 + i * 0.06 }} className="glass p-5">
              <div className="h-9 w-9 rounded-lg bg-cyan-300/15 text-cyan-300 grid place-items-center"><Icon className="h-4 w-4" /></div>
              <div className="text-1 text-sm font-semibold mt-3">{x.t}</div>
              <div className="text-3 text-xs mt-1">{x.d}</div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

// =====================================================================
// Slide 13 — Family Advisory
// =====================================================================
function FamilyAdvisorySlide() {
  return (
    <div className="relative grid lg:grid-cols-12 gap-10 items-center">
      <LayerBadge n={4} />
      <div className="lg:col-span-7">
        <Eyebrow>Family advisory</Eyebrow>
        <h2 className="text-3xl sm:text-5xl font-semibold tracking-tight text-1 leading-tight">
          We guide the family — because they <span className="text-cyan-300">impact the outcome.</span>
        </h2>
        <ul className="mt-7 space-y-2.5">
          {[
            "Decision filtering — we vet every offer that hits the kitchen table",
            "Expectation management — round, money, role, timeline",
            "Communication alignment — one voice across the camp",
          ].map((b) => (
            <li key={b} className="flex items-center gap-3 text-2 text-sm sm:text-base">
              <Check className="h-4 w-4 text-emerald-300 shrink-0" />
              {b}
            </li>
          ))}
        </ul>
      </div>
      <div className="lg:col-span-5">
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
          <div className="text-3 text-[10px] uppercase tracking-wider mb-3">Family inbox · this week</div>
          {[
            { who: "Agent", what: "3 representation pitches received — vetted by us first" },
            { who: "Combine", what: "Recommendation: skip 40, run pro day instead" },
            { who: "Media", what: "Athletic feature — talking points provided" },
          ].map((m, i) => (
            <div key={i} className="flex items-start gap-3 py-2 border-t border-white/5 first:border-0">
              <div className="h-7 w-7 rounded-full bg-cyan-300/15 text-cyan-300 grid place-items-center text-[10px] font-semibold">{m.who[0]}</div>
              <div className="flex-1 min-w-0">
                <div className="text-1 text-xs font-medium">{m.who}</div>
                <div className="text-2 text-xs">{m.what}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// NEW · Slide 6 — Why Families Hire Us (the 8 parent questions)
// Inserted 2026-02-08 between "The 4 Layers" and "Inside L1+L2".
// This single slide essentially sells itself for parents mid-decision-
// fatigue. Reading the 8 questions out loud is the close.
// =====================================================================
function WhyHireSlide() {
  const QUESTIONS = [
    { q: "Is my son truly draftable?",                  tone: "cyan",  icon: ClipboardList, layer: "L1 · Evaluation" },
    { q: "Should he declare — or return to school?",    tone: "dark",  icon: Target,        layer: "L3 · Strategy" },
    { q: "Should he transfer?",                         tone: "glass", icon: TrendingUp,    layer: "L3 · Strategy" },
    { q: "What are NFL teams actually saying?",         tone: "rose",  icon: Eye,           layer: "L1 · Evaluation" },
    { q: "Which agent should we choose?",               tone: "dark",  icon: Briefcase,     layer: "L3 · Strategy" },
    { q: "What concerns do teams have?",                tone: "glass", icon: Shield,        layer: "L1 · Evaluation" },
    { q: "How do we maximize opportunities?",           tone: "cyan",  icon: Sparkles,      layer: "L2 · Preparation" },
    { q: "What mistakes should we avoid?",              tone: "cream", icon: Heart,         layer: "L4 · Family" },
  ];

  return (
    <MouseParallaxStage>
      <div className="flex items-center gap-3 mb-6">
        <BentoTag tone="rose">Why families hire us</BentoTag>
        <span className="text-3 text-[10px] uppercase tracking-[0.22em]">06 · 12</span>
      </div>

      <BentoHeadline className="max-w-[1100px]" parallax={3}>
        The questions parents are <span className="text-cyan-300 italic font-light">actually</span> asking —<br />
        the ones nobody else is answering.
      </BentoHeadline>

      <p className="text-2 text-base sm:text-lg mt-5 max-w-3xl leading-relaxed">
        Every family we work with arrives carrying the same set of questions. We exist to answer them with data, experience, and a former front-office point of view.
      </p>

      <div className="mt-9 grid grid-cols-12 gap-3 sm:gap-4">
        {QUESTIONS.map((Q, i) => {
          const Icon = Q.icon;
          const onLightTone = Q.tone === "cream";
          const onCyan = Q.tone === "cyan";
          return (
            <BentoTile
              key={Q.q}
              tone={Q.tone}
              parallax={9 + (i % 3)}
              delay={0.04 + i * 0.045}
              className="col-span-12 sm:col-span-6 lg:col-span-3 p-5 sm:p-6 min-h-[170px] flex flex-col justify-between relative"
              testId={`why-hire-q-${i + 1}`}
            >
              <div className="flex items-start justify-between gap-2">
                <Icon
                  className={`h-5 w-5 ${onLightTone ? "text-rose-500/80" : onCyan ? "" : "text-cyan-300"}`}
                  strokeWidth={1.7}
                />
                <span
                  className={`font-semibold tabular-nums tracking-tighter leading-none select-none ${
                    onLightTone ? "text-[#0E1116]/30" : onCyan ? "text-[#04111A]/35" : "text-white/25"
                  }`}
                  style={{ fontSize: "clamp(1.4rem, 2.2vw, 2rem)" }}
                  aria-hidden="true"
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>
              <div className="mt-3">
                <p className={`font-semibold tracking-tight leading-[1.15] ${onLightTone || onCyan ? "" : "text-1"}`} style={{ fontSize: "clamp(0.95rem, 1.2vw, 1.05rem)" }}>
                  "{Q.q}"
                </p>
                <div
                  className={`mt-3 text-[10px] uppercase tracking-[0.18em] ${
                    onLightTone ? "text-[#0E1116]/55" : onCyan ? "opacity-75" : "text-3"
                  }`}
                >
                  {Q.layer}
                </div>
              </div>
            </BentoTile>
          );
        })}
      </div>

      <div className="mt-7 text-3 text-[11px] uppercase tracking-[0.22em] italic">
        We answer each with data, experience, and a former front-office point of view.
      </div>
    </MouseParallaxStage>
  );
}

// =====================================================================
// NEW · Slide 7 — Inside Layers 1 & 2 (Evaluation + Preparation)
// Compresses former slides 7 (Intel Report), 10 (Performance), 11 (Interview Prep)
// =====================================================================
function InsideEvaluationPrepSlide() {
  const evalTiles = [
    { icon: ClipboardList, title: "Player Intelligence Report", line: "1.0–9.0 grade · 8 evaluation lenses · how the league sees him." },
    { icon: TrendingUp,    title: "Scheme Fit & Comp",          line: "Where his tape lives. Who he projects to. Floor vs ceiling." },
    { icon: BarChart3,     title: "Per-Game Trend Grades",      line: "Every game scored. The line teams actually watch." },
  ];
  const prepTiles = [
    { icon: Activity, title: "Performance Team",       line: "Position coach · S&C · speed · sports medicine — orchestrated." },
    { icon: Mic,      title: "Interview & Whiteboard", line: "Combine, formal, informal — six-dimension live AI scoring." },
    { icon: Eye,      title: "Media & Comms",          line: "Sound-bite ready. Scout-room aware. One voice across camp." },
  ];

  return (
    <MouseParallaxStage>
      <div className="flex items-center gap-3 mb-6">
        <BentoTag tone="cyan">Inside the layers</BentoTag>
        <span className="text-3 text-[10px] uppercase tracking-[0.22em]">07 · 12</span>
      </div>
      <BentoHeadline className="max-w-[1100px]" parallax={3}>
        Evaluation. <span className="bg-gradient-to-r from-cyan-300 to-blue-400 bg-clip-text text-transparent italic font-light">Preparation.</span>
      </BentoHeadline>
      <p className="text-2 text-base sm:text-lg mt-4 max-w-3xl">
        Where the tape gets graded the way teams grade it — and where we sharpen what shows up on tape, in the room, and on the mic.
      </p>

      <div className="mt-9 grid grid-cols-12 gap-3 sm:gap-4">
        {/* Layer 1 cluster — cream anchor + 3 tiles */}
        <BentoTile tone="cream" parallax={8} delay={0.05} className="col-span-12 lg:col-span-3 row-span-2 p-7 flex flex-col justify-between min-h-[300px] relative">
          <div>
            <BentoTag tone="dark">Layer 01</BentoTag>
            <h3 className="text-2xl sm:text-[2.1rem] font-semibold leading-[1.05] tracking-tight mt-5">Evaluation</h3>
            <p className="text-2 text-sm mt-3 leading-relaxed">
              The intelligence the family never had access to. The same data front offices use to decide.
            </p>
            <p className="text-2 text-[11px] italic mt-4 leading-snug border-l-2 border-rose-500/50 pl-2.5 max-w-[14rem]">
              "Truth creates opportunity. False hope creates disappointment."
            </p>
          </div>
          <div className="mt-6 flex items-end justify-between">
            <ClipboardList className="h-10 w-10 opacity-70" strokeWidth={1.4} />
            <div className="font-semibold tabular-nums tracking-tighter leading-none" style={{ fontSize: "clamp(2.6rem, 5.4vw, 4.2rem)" }}>9.0</div>
          </div>
          <div className="absolute top-5 right-5"><Asterisk tone="rose" size={28} /></div>
        </BentoTile>

        {evalTiles.map((t, i) => {
          const Icon = t.icon;
          return (
            <BentoTile
              key={t.title}
              tone={i === 0 ? "cyan" : (i === 1 ? "dark" : "glass")}
              parallax={12 + i * 2}
              delay={0.10 + i * 0.05}
              className="col-span-12 sm:col-span-6 lg:col-span-3 p-6 min-h-[145px]"
            >
              <Icon className={`h-5 w-5 ${i === 0 ? "" : "text-cyan-300"}`} strokeWidth={1.6} />
              <div className={`mt-3 text-base font-semibold leading-snug ${i === 0 ? "" : "text-1"}`}>{t.title}</div>
              <div className={`mt-1.5 text-[12px] leading-relaxed ${i === 0 ? "opacity-80" : "text-3"}`}>{t.line}</div>
            </BentoTile>
          );
        })}

        {/* Layer 2 cluster — dark anchor + 3 tiles */}
        <BentoTile tone="dark" parallax={8} delay={0.30} className="col-span-12 lg:col-span-3 row-span-2 p-7 flex flex-col justify-between min-h-[300px] relative">
          <div>
            <BentoTag tone="outline">Layer 02</BentoTag>
            <h3 className="text-1 text-2xl sm:text-[2.1rem] font-semibold leading-[1.05] tracking-tight mt-5">Preparation</h3>
            <p className="text-3 text-sm mt-3 leading-relaxed">
              We sharpen what shows up on tape — and what comes out of his mouth in the room.
            </p>
            <p className="text-3 text-[11px] italic mt-4 leading-snug border-l-2 border-cyan-300/60 pl-2.5 max-w-[14rem]">
              "One poor interview can change perception. One great interview can create opportunity."
            </p>
          </div>
          <div className="mt-6 flex items-end justify-between">
            <Activity className="h-10 w-10 text-cyan-300 opacity-80" strokeWidth={1.4} />
            <div className="text-cyan-200 font-semibold tabular-nums tracking-tighter leading-none" style={{ fontSize: "clamp(2.6rem, 5.4vw, 4.2rem)" }}>6×</div>
          </div>
          <div className="absolute top-5 right-5"><Asterisk tone="cyan" size={28} /></div>
        </BentoTile>

        {prepTiles.map((t, i) => {
          const Icon = t.icon;
          return (
            <BentoTile
              key={t.title}
              tone={i === 0 ? "glass" : (i === 1 ? "cyan" : "dark")}
              parallax={10 + i * 2}
              delay={0.35 + i * 0.05}
              className="col-span-12 sm:col-span-6 lg:col-span-3 p-6 min-h-[145px]"
            >
              <Icon className={`h-5 w-5 ${i === 1 ? "" : "text-cyan-300"}`} strokeWidth={1.6} />
              <div className={`mt-3 text-base font-semibold leading-snug ${i === 1 ? "" : "text-1"}`}>{t.title}</div>
              <div className={`mt-1.5 text-[12px] leading-relaxed ${i === 1 ? "opacity-80" : "text-3"}`}>{t.line}</div>
            </BentoTile>
          );
        })}
      </div>
    </MouseParallaxStage>
  );
}

// =====================================================================
// NEW · Slide 7 — Inside Layers 3 & 4 (Strategy + Family)
// Compresses former slides 8 (Draft Strategy), 9 (Team Blueprint),
// 12 (Business & Brand), 13 (Family Advisory)
// =====================================================================
function InsideStrategyFamilySlide() {
  const stratTiles = [
    { icon: Target,    title: "Range Modeling",       line: "R1 / R2 / R3 with confidence — not guesswork." },
    { icon: Building,  title: "32-Team Fit Matrix",   line: "Scheme + roster opportunity, scored across the league." },
    { icon: Briefcase, title: "Agent & Business Plan",line: "Vetted representation. Bonus modeling. Long-game career." },
    { icon: Megaphone, title: "Brand & Narrative",    line: "Position him as a portfolio, not a logo. Shape the room." },
  ];
  const famTiles = [
    { icon: Heart,        title: "Family Advisory",     line: "Every fork in the road. Quietly, in private." },
    { icon: MessageSquare,title: "Decision Filtering",  line: "We vet every offer that hits the kitchen table." },
    { icon: Shield,       title: "Expectation Mgmt",    line: "Round, money, role, timeline — aligned, week by week." },
  ];

  return (
    <MouseParallaxStage>
      <div className="flex items-center gap-3 mb-6">
        <BentoTag tone="rose">Inside the layers</BentoTag>
        <span className="text-3 text-[10px] uppercase tracking-[0.22em]">08 · 12</span>
      </div>
      <BentoHeadline className="max-w-[1100px]" parallax={3}>
        Strategy. <span className="text-rose-300 italic font-light">Family.</span>
      </BentoHeadline>
      <p className="text-2 text-base sm:text-lg mt-4 max-w-3xl">
        Where the actual draft outcome gets shaped — round, team, contract, story — and the people behind him are guided through every decision that matters.
      </p>

      <div className="mt-9 grid grid-cols-12 gap-3 sm:gap-4">
        {/* Layer 3 anchor */}
        <BentoTile tone="cyan" parallax={8} delay={0.05} className="col-span-12 lg:col-span-3 row-span-2 p-7 flex flex-col justify-between min-h-[300px] relative">
          <div>
            <BentoTag tone="dark">Layer 03</BentoTag>
            <h3 className="text-2xl sm:text-[2.1rem] font-semibold leading-[1.05] tracking-tight mt-5">Strategy</h3>
            <p className="text-sm mt-3 leading-relaxed opacity-85">
              We don't guess the draft. We model it. Round, team, agent, bonus — every fork mapped before it arrives.
            </p>
            <p className="text-[11px] italic mt-4 leading-snug border-l-2 border-[#04111A]/40 pl-2.5 max-w-[14rem] opacity-90">
              "Getting drafted is important. Getting drafted into the right situation is critical."
            </p>
          </div>
          <div className="mt-6 flex items-end justify-between">
            <Target className="h-10 w-10 opacity-80" strokeWidth={1.4} />
            <div className="font-semibold tabular-nums tracking-tighter leading-none" style={{ fontSize: "clamp(2.6rem, 5.4vw, 4.2rem)" }}>32</div>
          </div>
          <div className="absolute top-5 right-5"><Asterisk tone="dark" size={28} /></div>
        </BentoTile>

        {stratTiles.map((t, i) => {
          const Icon = t.icon;
          const tones = ["dark", "glass", "dark", "glass"];
          return (
            <BentoTile
              key={t.title}
              tone={tones[i]}
              parallax={12 + i * 2}
              delay={0.10 + i * 0.05}
              className="col-span-12 sm:col-span-6 lg:col-span-3 p-6 min-h-[145px]"
            >
              <Icon className="h-5 w-5 text-cyan-300" strokeWidth={1.6} />
              <div className="mt-3 text-1 text-base font-semibold leading-snug">{t.title}</div>
              <div className="mt-1.5 text-3 text-[12px] leading-relaxed">{t.line}</div>
            </BentoTile>
          );
        })}

        {/* Layer 4 anchor — cream/rose accent */}
        <BentoTile tone="cream" parallax={8} delay={0.30} className="col-span-12 lg:col-span-3 row-span-2 p-7 flex flex-col justify-between min-h-[300px] relative">
          <div>
            <BentoTag tone="dark">Layer 04</BentoTag>
            <h3 className="text-2xl sm:text-[2.1rem] font-semibold leading-[1.05] tracking-tight mt-5">Family</h3>
            <p className="text-2 text-sm mt-3 leading-relaxed">
              The family impacts the outcome. We brief them on every fork — quietly, in private.
            </p>
            <p className="text-2 text-[11px] italic mt-4 leading-snug border-l-2 border-rose-500/50 pl-2.5 max-w-[14rem]">
              "Families influence outcomes more than they realize."
            </p>
          </div>
          <div className="mt-6 flex items-end justify-between">
            <Heart className="h-10 w-10 text-rose-500 opacity-80" strokeWidth={1.4} />
            <div className="font-semibold tabular-nums tracking-tighter leading-none" style={{ fontSize: "clamp(2.6rem, 5.4vw, 4.2rem)" }}>1:1</div>
          </div>
          <div className="absolute top-5 right-5"><Asterisk tone="rose" size={28} /></div>
        </BentoTile>

        {famTiles.map((t, i) => {
          const Icon = t.icon;
          const tones = ["dark", "glass", "cyan"];
          return (
            <BentoTile
              key={t.title}
              tone={tones[i]}
              parallax={10 + i * 2}
              delay={0.35 + i * 0.05}
              className="col-span-12 sm:col-span-6 lg:col-span-3 p-6 min-h-[145px]"
            >
              <Icon className={`h-5 w-5 ${tones[i] === "cyan" ? "" : "text-cyan-300"}`} strokeWidth={1.6} />
              <div className={`mt-3 text-base font-semibold leading-snug ${tones[i] === "cyan" ? "" : "text-1"}`}>{t.title}</div>
              <div className={`mt-1.5 text-[12px] leading-relaxed ${tones[i] === "cyan" ? "opacity-80" : "text-3"}`}>{t.line}</div>
            </BentoTile>
          );
        })}
      </div>
    </MouseParallaxStage>
  );
}

// =====================================================================
// Slide 14 — Platform UI Mock
// =====================================================================
function PlatformSlide() {
  return (
    <div>
      <Eyebrow>The platform</Eyebrow>
      <h2 className="text-3xl sm:text-5xl font-semibold tracking-tight text-1 leading-tight max-w-4xl">
        OmniDraftLab — <span className="text-cyan-300">live workspace.</span>
      </h2>
      <p className="text-2 text-base mt-3 max-w-2xl">Everything we just walked you through — in a single dashboard, available 24/7 to athlete and family.</p>

      {/* "Process, not moment" caption — frames the platform as the through-line
          across the entire cycle, not a one-shot deliverable. */}
      <div
        className="mt-4 inline-flex items-center gap-2 text-3 text-[11px] sm:text-[12px] italic max-w-3xl border-l-2 border-cyan-300/50 pl-3"
        data-testid="platform-journey-caption"
      >
        "The draft process is not a moment. <span className="text-cyan-300 not-italic font-semibold">It is a journey.</span>"
      </div>
      <div className="mt-8 rounded-2xl border border-white/10 bg-[color:var(--canvas-2)]/70 p-3 sm:p-4">
        <div className="rounded-xl border border-white/10 overflow-hidden">
          {/* Mock app shell */}
          <div className="flex items-center gap-1.5 px-3 py-2 border-b border-white/10 bg-white/[0.03]">
            <span className="h-2.5 w-2.5 rounded-full bg-rose-400/70" />
            <span className="h-2.5 w-2.5 rounded-full bg-amber-400/70" />
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/70" />
            <div className="ml-3 text-3 text-[10px] tracking-wider">omnidraftlab.app</div>
          </div>
          <div className="grid grid-cols-12 gap-2 p-3">
            <div className="col-span-3 rounded-lg bg-white/[0.04] p-3">
              <div className="text-3 text-[9px] uppercase tracking-wider">Nav</div>
              {["Dashboard", "Player", "Evaluation", "Draft Plan", "Team Fits", "Reports", "AI Cockpit"].map((x, i) => (
                <div key={x} className={`text-xs py-1.5 ${i === 6 ? "text-cyan-300" : "text-2"}`}>· {x}</div>
              ))}
            </div>
            <div className="col-span-9 grid grid-cols-3 gap-2">
              <div className="col-span-3 rounded-lg bg-white/[0.04] p-3 flex items-center justify-between">
                <div>
                  <div className="text-3 text-[10px] uppercase tracking-wider">Live Overview</div>
                  <div className="text-1 text-sm font-semibold">Roster Pipeline · 4 players active</div>
                </div>
                <div className="hidden sm:flex gap-2">
                  {[42, 78, 91, 56, 73, 88].map((v, i) => <div key={i} className="w-1.5 rounded-t bg-cyan-300/70" style={{ height: `${v / 3}px` }} />)}
                </div>
              </div>
              <div className="rounded-lg bg-white/[0.04] p-3"><div className="text-3 text-[9px] uppercase">Pending</div><div className="text-1 text-2xl font-semibold mt-1">3</div></div>
              <div className="rounded-lg bg-white/[0.04] p-3"><div className="text-3 text-[9px] uppercase">Approved</div><div className="text-1 text-2xl font-semibold mt-1">12</div></div>
              <div className="rounded-lg bg-white/[0.04] p-3"><div className="text-3 text-[9px] uppercase">Published</div><div className="text-1 text-2xl font-semibold mt-1">8</div></div>
              <div className="col-span-3 rounded-lg bg-white/[0.04] p-3">
                <div className="text-3 text-[9px] uppercase tracking-wider mb-1">Recent AI activity</div>
                {["Player 360° published · Marcus Bell", "Team fit drafted · DEN", "Weekly digest approved · 2h ago"].map((x) => (
                  <div key={x} className="text-2 text-xs py-1 flex items-center gap-2"><Sparkles className="h-3 w-3 text-cyan-300" /> {x}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// Slide 15 — Process Timeline
// =====================================================================
function ProcessTimelineSlide() {
  const stages = [
    { n: "01", t: "Intake & Evaluation", d: "Tape, measurables, source intel" },
    { n: "02", t: "Strategy Build", d: "Range, fit matrix, narrative" },
    { n: "03", t: "Execution", d: "Camps, combine, pro day" },
    { n: "04", t: "Pre-Draft Peak", d: "Interviews, visits, final positioning" },
    { n: "05", t: "Draft & Transition", d: "Decisions, signing, rookie year" },
  ];
  return (
    <div>
      <Eyebrow>Process</Eyebrow>
      <h2 className="text-3xl sm:text-5xl font-semibold tracking-tight text-1 leading-tight max-w-4xl">
        From first conversation to <span className="text-cyan-300">draft night.</span>
      </h2>
      <div className="mt-12 relative">
        <div className="absolute left-0 right-0 top-7 h-px bg-gradient-to-r from-cyan-300/0 via-cyan-300/40 to-cyan-300/0 hidden lg:block" />
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {stages.map((s, i) => (
            <motion.div key={s.n} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 + i * 0.08 }} className="relative">
              <div className="h-14 w-14 mx-auto rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 text-[#04111A] grid place-items-center font-mono font-semibold text-sm shadow-[0_0_20px_rgba(34,211,238,0.35)]">{s.n}</div>
              <div className="mt-4 text-center">
                <div className="text-1 text-sm font-semibold">{s.t}</div>
                <div className="text-3 text-xs mt-1">{s.d}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// Slide 16 — Pricing Tiers
// =====================================================================
function PricingSlide() {
  const [copied, setCopied] = useState(false);
  const publicUrl = typeof window !== "undefined" ? `${window.location.origin}/pricing` : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch { /* clipboard API unavailable — fall through */ }
  };

  return (
    <MouseParallaxStage>
      <div className="min-h-[calc(100vh-180px)] flex flex-col justify-center py-8">
        <div className="flex items-center justify-between gap-4 flex-wrap mb-5">
          <div className="flex items-center gap-3">
            <BentoTag tone="cyan">Engagement tiers</BentoTag>
            <span className="text-3 text-[10px] uppercase tracking-[0.22em]">10 · 12</span>
          </div>
          <div className="flex items-center gap-2 no-print">
            <button
              data-testid="pricing-copy-link"
              onClick={copyLink}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs bg-white/[0.04] ring-1 ring-white/10 hover:ring-cyan-300/30 text-2 hover:text-1 transition"
            >
              <Share2 className="h-3.5 w-3.5" /> {copied ? "Link copied" : "Copy share link"}
            </button>
            <a
              href="/pricing"
              target="_blank"
              rel="noreferrer"
              data-testid="pricing-open-public"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs bg-white/[0.04] ring-1 ring-white/10 hover:ring-cyan-300/30 text-2 hover:text-1 transition"
            >
              <ExternalLink className="h-3.5 w-3.5" /> Open as page
            </a>
            <button
              data-testid="pricing-export-pdf"
              onClick={() => window.open("/pricing?print=1", "_blank")}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs bg-cyan-300 text-[#04111A] font-semibold hover:bg-cyan-200 transition"
            >
              <FileDown className="h-3.5 w-3.5" /> Export PDF
            </button>
          </div>
        </div>

        <div className="text-center max-w-4xl mx-auto">
          <BentoHeadline className="max-w-4xl mx-auto" parallax={3}>
            Built for <span className="text-cyan-300 italic font-light">serious</span> prospects only.
          </BentoHeadline>
          <p className="mt-4 text-2 text-sm sm:text-base leading-relaxed max-w-2xl mx-auto">
            Four engagement depths. Every tier is application-only — limited clients per draft class.
          </p>
        </div>

        <div className="mt-10 mx-auto w-full max-w-[1500px]">
          <PricingGrid compact />
        </div>

        <div className="text-3 text-[11px] mt-6 italic text-center max-w-2xl mx-auto no-print">
          Share the full pricing page with clients via the links above. Export to PDF opens a print-ready version.
        </div>
      </div>
    </MouseParallaxStage>
  );
}

// =====================================================================
// Slide 17 — Why This Matters (RED ACCENT)
// =====================================================================
function WhyMattersSlide() {
  // Big Idea — "Reduce Uncertainty. Maximize Opportunity."
  // The closing thesis. Replaces the older $$$ delta slide; the money beat
  // is already covered on slide 1 (the Problem).
  return (
    <MouseParallaxStage>
      <div className="flex items-center gap-3 mb-6">
        <BentoTag tone="cyan">The big idea</BentoTag>
        <span className="text-3 text-[10px] uppercase tracking-[0.22em]">11 · 12</span>
      </div>
      <BentoHeadline size="xl" className="max-w-5xl" parallax={3}>
        Reduce <span className="text-rose-400 italic font-light">uncertainty.</span><br />
        Maximize <span className="text-cyan-300 italic font-light">opportunity.</span>
      </BentoHeadline>

      <div className="mt-10 grid grid-cols-12 gap-3 sm:gap-4">
        {/* Left — the thesis statement, cream lead */}
        <BentoTile tone="cream" parallax={10} delay={0.05} className="col-span-12 lg:col-span-7 row-span-2 p-7 sm:p-9 flex flex-col justify-between min-h-[340px] relative">
          <div>
            <BentoTag tone="dark">What you can — and can't — control</BentoTag>
            <h3 className="text-2xl sm:text-[1.85rem] font-semibold tracking-tight mt-6 leading-[1.15] max-w-xl">
              Families cannot control the draft.<br />
              They can control <span className="italic font-light">preparation, information, and decision-making.</span>
            </h3>
            <p className="text-2 text-base mt-5 leading-relaxed max-w-xl">
              OmniDraftLab provides the intelligence, guidance, and strategic support needed to navigate the process with confidence.
            </p>
          </div>
          <div className="mt-7 flex items-end justify-between">
            <Sparkles className="h-9 w-9 opacity-60" strokeWidth={1.4} />
            <div className="text-right text-3 text-[10px] uppercase tracking-[0.22em] max-w-[180px]">
              Confidence · Clarity · Truth · Guidance · Access
            </div>
          </div>
          <div className="absolute top-6 right-6"><Asterisk tone="rose" size={40} /></div>
        </BentoTile>

        {/* Right top — controllable, cyan */}
        <BentoTile tone="cyan" parallax={14} delay={0.12} className="col-span-12 sm:col-span-6 lg:col-span-5 p-6 min-h-[160px] flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-medium">
              <Check className="h-3.5 w-3.5" strokeWidth={3} /> Controllable
            </div>
            <div className="text-lg font-semibold mt-3 leading-snug">
              Preparation. Information. Strategy. Positioning. Decisions.
            </div>
          </div>
          <div className="text-[11px] mt-3 opacity-85 italic">Where we operate.</div>
        </BentoTile>

        {/* Right bottom — not controllable, dark */}
        <BentoTile tone="dark" parallax={14} delay={0.18} className="col-span-12 sm:col-span-6 lg:col-span-5 p-6 min-h-[160px] flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-2 text-rose-300 text-[10px] uppercase tracking-[0.22em] font-medium">
              <X className="h-3.5 w-3.5" strokeWidth={3} /> Not controllable
            </div>
            <div className="text-1 text-lg font-semibold mt-3 leading-snug">
              Draft slot. Round. Team. Contract.
            </div>
          </div>
          <div className="text-3 text-[11px] mt-3 italic">Outcomes belong to 32 front offices.</div>
        </BentoTile>
      </div>

      <div className="mt-8 text-3 text-[12px] italic leading-relaxed max-w-3xl">
        Our responsibility is not to promise outcomes. Our responsibility is to ensure athletes and families are fully informed, fully prepared, and strategically positioned for every opportunity ahead.
      </div>
    </MouseParallaxStage>
  );
}

// =====================================================================
// Slide 18 — Long-Term Vision
// =====================================================================
function VisionSlide() {
  return (
    <MouseParallaxStage>
      <div className="flex items-center gap-3 mb-6">
        <BentoTag tone="cyan">Long-term vision</BentoTag>
        <span className="text-3 text-[10px] uppercase tracking-[0.22em]">18 · 19</span>
      </div>
      <BentoHeadline className="max-w-5xl" parallax={3}>
        Building the<br />
        <span className="text-cyan-300 italic font-light">pre-draft</span> standard.
      </BentoHeadline>

      <div className="mt-9 grid grid-cols-12 gap-3 sm:gap-4">
        {/* Platform — cream pillar */}
        <BentoTile tone="cream" parallax={8} delay={0.05} className="col-span-12 lg:col-span-5 row-span-2 p-7 sm:p-8 flex flex-col justify-between min-h-[300px] relative">
          <div>
            <BentoTag tone="dark">North star</BentoTag>
            <Building className="h-9 w-9 mt-7 opacity-80" strokeWidth={1.4} />
            <h3 className="text-3xl sm:text-4xl font-semibold tracking-tight mt-5 leading-tight">
              Platform Scaling
            </h3>
            <p className="text-2 text-sm mt-3 leading-relaxed max-w-md">
              OmniDraftLab as the operating system for elite prospects — every agent, every team, every cycle.
            </p>
          </div>
          <div className="mt-7 flex items-end justify-between">
            <div className="font-semibold tabular-nums tracking-tighter leading-none" style={{ fontSize: "clamp(2.6rem, 5.8vw, 4.6rem)" }}>2026 →</div>
            <div className="text-3 text-[11px] uppercase tracking-[0.2em] max-w-[140px] text-right">Multi-class. Multi-position.</div>
          </div>
          <div className="absolute top-5 right-5"><Asterisk tone="cyan" size={32} /></div>
        </BentoTile>

        {/* Team licensing — cyan */}
        <BentoTile tone="cyan" parallax={14} delay={0.12} className="col-span-12 sm:col-span-6 lg:col-span-4 p-6 min-h-[145px]">
          <BentoTag tone="dark">Pillar 02</BentoTag>
          <Users className="h-5 w-5 mt-4" strokeWidth={1.6} />
          <div className="text-lg font-semibold mt-3 leading-tight">Team Licensing</div>
          <div className="text-[11px] mt-1 opacity-80 leading-relaxed">Handpicked advisor groups in major markets.</div>
        </BentoTile>

        {/* Agent partnerships — dark */}
        <BentoTile tone="dark" parallax={14} delay={0.18} className="col-span-12 sm:col-span-6 lg:col-span-3 p-6 min-h-[145px]">
          <BentoTag tone="outline">Pillar 03</BentoTag>
          <Briefcase className="h-5 w-5 text-cyan-300 mt-4" strokeWidth={1.6} />
          <div className="text-1 text-lg font-semibold mt-3 leading-tight">Agent Tier-1</div>
          <div className="text-3 text-[11px] mt-1 leading-relaxed">Plug into our intelligence layer.</div>
        </BentoTile>

        {/* Data ecosystem — glass */}
        <BentoTile tone="glass" parallax={10} delay={0.24} className="col-span-12 sm:col-span-6 lg:col-span-4 p-6">
          <BentoTag tone="cream">Pillar 04</BentoTag>
          <BarChart3 className="h-5 w-5 text-cyan-300 mt-4" strokeWidth={1.6} />
          <div className="text-1 text-lg font-semibold mt-3 leading-tight">Data Ecosystem</div>
          <div className="text-3 text-[11px] mt-1 leading-relaxed">First-party draft + combine data — proprietary moat.</div>
        </BentoTile>

        {/* Closing dark stat */}
        <BentoTile tone="dark" parallax={6} delay={0.30} className="col-span-12 sm:col-span-6 lg:col-span-3 p-6 min-h-[120px] flex flex-col justify-center">
          <div className="text-cyan-200 text-[10px] uppercase tracking-wider">Outcome</div>
          <div className="text-1 text-2xl font-semibold mt-1 leading-tight">The new<br />pre-draft standard.</div>
        </BentoTile>
      </div>
    </MouseParallaxStage>
  );
}

// =====================================================================
// Slide 19 — CTA
// =====================================================================
function CtaSlide() {
  // Logged-in admins/families can shortcut straight to the workspace from
  // the closing slide instead of being asked to sign in again.
  const { token } = useAuth();
  const isAuthed = !!token;
  return (
    <MouseParallaxStage>
      <div className="flex items-center gap-3 mb-6">
        <BentoTag tone="cyan">Apply · 12 · 12</BentoTag>
        <span className="text-3 text-[10px] uppercase tracking-[0.22em]">Ready to be prepared?</span>
      </div>

      <div className="grid grid-cols-12 gap-3 sm:gap-4">
        {/* Big editorial CTA — left */}
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 0.65, 0.2, 1] }}
          className="col-span-12 lg:col-span-7 relative"
        >
          <BentoHeadline size="xl" parallax={4}>
            For serious athletes<br />
            and families <span className="text-cyan-300 italic font-light">only.</span>
          </BentoHeadline>
          <div className="mt-6 max-w-2xl border-l-2 border-cyan-300/60 pl-4">
            <p className="text-1 text-base sm:text-lg italic leading-snug font-light">
              We are not selling hope. We are providing <span className="text-cyan-300 not-italic font-semibold">clarity.</span>
            </p>
            <p className="text-3 text-[12px] mt-2 italic leading-relaxed">
              Our responsibility is to ensure athletes and families are fully informed, fully prepared, and strategically positioned for every opportunity ahead.
            </p>
          </div>
          <p className="text-2 text-base sm:text-lg mt-6 max-w-2xl">
            Limited clients per draft class. Application-based intake. Premium positioning, end-to-end.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <a
              href="mailto:hello@omnidraftlab.com"
              data-testid="pitch-cta-apply"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-2xl bg-cyan-300 text-[#04111A] font-semibold hover:bg-cyan-200 transition shadow-[0_18px_50px_-15px_rgba(34,211,238,0.7)] text-base"
            >
              Apply to Work With OmniDraftLab <ArrowRight className="h-4 w-4" />
            </a>
            <Link
              to={isAuthed ? "/app/dashboard" : "/login"}
              data-testid={isAuthed ? "pitch-cta-dashboard" : "pitch-cta-signin"}
              className="inline-flex items-center gap-2 px-7 py-4 rounded-2xl border border-white/15 bg-white/[0.03] text-1 hover:bg-white/[0.08] transition text-base"
            >
              <Hexagon className="h-4 w-4" /> {isAuthed ? "Open the dashboard" : "Open the workspace"}
            </Link>
          </div>
          <div className="absolute -top-2 right-2 hidden lg:block"><Asterisk tone="rose" size={44} /></div>
        </motion.div>

        {/* Cream contact card — right */}
        <BentoTile tone="cream" parallax={12} delay={0.18} className="col-span-12 lg:col-span-5 p-7 sm:p-8 flex flex-col justify-between min-h-[300px] relative">
          <div>
            <BentoTag tone="dark">Get in touch</BentoTag>
            <h3 className="text-2xl sm:text-3xl font-semibold tracking-tight mt-6 leading-tight">
              Quiet conversations.<br />Serious outcomes.
            </h3>
          </div>
          <div className="mt-7 space-y-3.5">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-xl bg-[#0E1116] text-[#F4EFE6] grid place-items-center">
                <Phone className="h-4 w-4" strokeWidth={1.6} />
              </div>
              <div>
                <div className="text-3 text-[10px] uppercase tracking-[0.2em]">Direct</div>
                <div className="text-1 text-sm font-semibold">hello@omnidraftlab.com</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-xl bg-[#0E1116] text-[#F4EFE6] grid place-items-center">
                <Lock className="h-4 w-4" strokeWidth={1.6} />
              </div>
              <div>
                <div className="text-3 text-[10px] uppercase tracking-[0.2em]">Privacy</div>
                <div className="text-1 text-sm font-semibold">Application-only · NDA available</div>
              </div>
            </div>
          </div>
          <div className="absolute top-5 right-5"><Asterisk tone="rose" size={36} /></div>
        </BentoTile>
      </div>

      <div className="mt-10 text-3 text-[11px] uppercase tracking-[0.22em]">
        Use ← → keys to navigate · Esc to exit · F for fullscreen · P to print/PDF
      </div>
    </MouseParallaxStage>
  );
}

// =====================================================================
// Shared eyebrow components
// =====================================================================
function Eyebrow({ children }) {
  return (
    <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-cyan-300 mb-4">
      <span className="h-px w-8 bg-cyan-300/60" />{children}
    </div>
  );
}
function EyebrowRed({ children }) {
  return (
    <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-rose-400 mb-4">
      <span className="h-px w-8 bg-rose-400/60" />{children}
    </div>
  );
}

// LayerBadge — small pill that ties a feature slide back to one of the four
// layers introduced on slide 06. Sits on the slide's top-right corner so it
// doesn't compete with the slide's own eyebrow / headline.
const LAYER_META = {
  1: { label: "Evaluation", tone: "cyan" },
  2: { label: "Preparation", tone: "outline" },
  3: { label: "Strategy", tone: "rose" },
  4: { label: "Family", tone: "cream" },
};
function LayerBadge({ n, className = "" }) {
  const m = LAYER_META[n];
  if (!m) return null;
  const tones = {
    cyan: "bg-cyan-300/15 text-cyan-200 ring-cyan-300/30",
    rose: "bg-rose-400/15 text-rose-200 ring-rose-300/30",
    cream: "bg-[#F4EFE6] text-[#0E1116] ring-transparent",
    outline: "bg-white/[0.04] text-1 ring-white/15",
  }[m.tone];
  return (
    <span
      data-testid={`layer-badge-${n}`}
      className={`absolute top-5 right-5 z-10 inline-flex items-center gap-2 rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.22em] font-medium ring-1 ${tones} ${className}`}
    >
      <span className="font-mono opacity-70">L{n}</span>
      <span>{m.label}</span>
    </span>
  );
}

// =====================================================================
// Slide registry
// =====================================================================
//
// 2026-02-07 — Pitch deck condensed from 19 → 11 slides for parent audiences.
// Removed slides (functions retained above for easy restoration):
//   - GapSlide                 → merged into ProblemSlide (bottom strip)
//   - IntelReportSlide  (L1)   ─┐
//   - PerformanceSlide  (L2)    │ → compressed into InsideEvaluationPrepSlide
//   - InterviewPrepSlide(L2)   ─┘
//   - DraftStrategySlide(L3)   ─┐
//   - TeamBlueprintSlide(L3)    │
//   - BusinessBrandSlide(L3)    │ → compressed into InsideStrategyFamilySlide
//   - FamilyAdvisorySlide(L4)  ─┘
//   - ProcessTimelineSlide     → cut (Platform slide carries enough)
//   - VisionSlide              → cut (parent audience: today, not 5-yr roadmap)
//

// =====================================================================
// 00 · Cover — title page (video-still bg) shown before the deck.
// Mirrors the public landing hero so a parent opens to a branded title
// page, then taps "Begin pitch" to roll into The Problem.
// =====================================================================
function CoverSlide({ onBegin }) {
  return (
    <div className="relative w-full" data-testid="pitch-cover-slide">
      {/* Background video — same treatment as the public landing hero.
          print:hidden so the existing PDF export stays clean (a fixed
          element would otherwise repeat on every printed page). */}
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden print:hidden">
        <video
          className="absolute inset-0 w-full h-full object-cover opacity-55"
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          poster="/pitch/hero-poster.jpg"
        >
          <source src="/pitch/hero.webm" type="video/webm" />
          <source src="/pitch/hero.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-[#04111A]/60" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#04111A]/20 to-[#04111A]/85" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#04111A]/65 via-transparent to-transparent" />
      </div>

      <MouseParallaxStage className="relative w-full">
        <div className="grid grid-cols-12 gap-4 sm:gap-5 items-stretch">
          {/* Editorial headline tile */}
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 0.65, 0.2, 1] }}
            className="col-span-12 lg:col-span-8 relative"
          >
            <div className="flex items-center gap-3 mb-6">
              <BentoTag tone="cyan">OmniDraftLab</BentoTag>
              <span className="text-3 text-[10px] uppercase tracking-[0.22em]">Pitch · 2026</span>
            </div>
            <BentoHeadline size="xl" parallax={4} className="drop-shadow-[0_4px_24px_rgba(0,0,0,0.6)]">
              Pre-Draft<br />
              <span className="bg-gradient-to-r from-cyan-300 via-blue-400 to-blue-500 bg-clip-text text-transparent">Intelligence.</span><br />
              Strategy. <span className="italic font-light">Positioning.</span>
            </BentoHeadline>
            <p className="text-2 text-base sm:text-xl mt-7 max-w-2xl drop-shadow-[0_2px_12px_rgba(0,0,0,0.5)]">
              We prepare athletes and families for how the NFL <em>actually</em> works.
            </p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4, ease: [0.22, 0.65, 0.2, 1] }}
              className="mt-8"
            >
              <button
                type="button"
                data-testid="pitch-begin"
                onClick={onBegin}
                className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl bg-cyan-300 text-[#04111A] font-semibold hover:bg-cyan-200 transition shadow-[0_18px_50px_-15px_rgba(34,211,238,0.7)] text-base"
              >
                Begin pitch <ArrowRight className="h-4 w-4" />
              </button>
            </motion.div>

            <div className="absolute -top-2 right-4 hidden lg:block">
              <Asterisk tone="rose" size={48} />
            </div>
          </motion.div>

          {/* Metric stack — right column */}
          <div className="col-span-12 lg:col-span-4 grid grid-cols-2 lg:grid-cols-1 gap-3 sm:gap-4">
            <MetricTile
              tone="cream"
              eyebrow="NFL personnel"
              value="20"
              label="Years inside the league"
              sub="Carolina · Tennessee"
              parallax={10}
              delay={0.15}
              testId="cover-metric-years"
            />
            <MetricTile
              tone="cyan"
              eyebrow="Engagement"
              value="01"
              label="Application-only"
              sub="Limited clients per draft class"
              parallax={14}
              delay={0.25}
              testId="cover-metric-app"
            />
          </div>
        </div>

        {/* Eyebrow row */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.45, ease: [0.22, 0.65, 0.2, 1] }}
          className="mt-12 flex flex-wrap items-center gap-x-6 gap-y-2 text-3 text-[11px] uppercase tracking-[0.22em]"
        >
          <span>NFL-Caliber Evaluation</span>
          <span className="text-3">·</span>
          <span>Front-Office Strategy</span>
          <span className="text-3">·</span>
          <span>Family Advisory</span>
        </motion.div>

        {/* Pull-quote */}
        <BentoTile
          tone="glass"
          parallax={6}
          delay={0.5}
          testId="cover-pullquote"
          className="mt-6 max-w-xl p-4 sm:p-5 relative"
        >
          <Quote className="h-3.5 w-3.5 text-cyan-300/80 absolute top-4 left-4" strokeWidth={1.6} />
          <blockquote className="text-1 text-sm sm:text-[15px] italic font-light leading-snug pl-7 pr-1">
            They told us things our agent never did — and they told us
            <span className="text-cyan-300 not-italic font-semibold"> early.</span>
            <span className="block mt-1 not-italic font-light text-2 text-[12px] leading-relaxed">
              By the time the call came, our son knew exactly who was on the line.
            </span>
          </blockquote>
          <figcaption className="mt-3 pl-7 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span className="text-1 text-[11px] font-semibold">— Mother of a recent draft prospect</span>
          </figcaption>
        </BentoTile>
      </MouseParallaxStage>
    </div>
  );
}

const SLIDES = [
  { id: "cover",         Comp: CoverSlide,                    label: "Cover" },
  { id: "problem",       Comp: ProblemSlide,                  label: "The Problem" },
  { id: "solution",      Comp: SolutionSlide,                 label: "The Solution" },
  { id: "authority",     Comp: AuthoritySlide,                label: "Authority" },
  { id: "promise",       Comp: PromiseSlide,                  label: "The Guarantee" },
  { id: "what",          Comp: WhatWeDoSlide,                 label: "The 4 Layers" },
  { id: "why-hire",      Comp: WhyHireSlide,                  label: "Why Families Hire Us" },
  { id: "inside-eval",   Comp: InsideEvaluationPrepSlide,     label: "Inside L1 & L2" },
  { id: "inside-strat",  Comp: InsideStrategyFamilySlide,     label: "Inside L3 & L4" },
  { id: "platform",      Comp: PlatformSlide,                 label: "Platform" },
  { id: "pricing",       Comp: PricingSlide,                  label: "Pricing" },
  { id: "why",           Comp: WhyMattersSlide,               label: "The Big Idea" },
  { id: "cta",           Comp: CtaSlide,                      label: "Apply" },
];

// =====================================================================
// Main page
// =====================================================================
export default function PitchDeckPage() {
  useDocumentTitle("OmniDraftLab · Pitch Deck");
  const navigate = useNavigate();
  const [idx, setIdx] = useState(0);
  const [fs, setFs] = useState(false);

  const total = SLIDES.length;
  const next = useCallback(() => setIdx((i) => Math.min(total - 1, i + 1)), [total]);
  const prev = useCallback(() => setIdx((i) => Math.max(0, i - 1)), []);

  const printDeck = () => window.print();
  const toggleFs = async () => {
    if (!document.fullscreenElement) {
      try { await document.documentElement.requestFullscreen(); setFs(true); } catch (_e) { /* noop */ }
    } else {
      try { await document.exitFullscreen(); setFs(false); } catch (_e) { /* noop */ }
    }
  };

  // Hide platform "Made with Emergent" badge on this page only
  useEffect(() => {
    document.body.classList.add("pitch-mode");
    return () => document.body.classList.remove("pitch-mode");
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " " || e.key === "Enter") { e.preventDefault(); next(); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
      else if (e.key === "Escape") { if (document.fullscreenElement) document.exitFullscreen(); else navigate("/"); }
      else if (e.key.toLowerCase() === "f") toggleFs();
      else if (e.key.toLowerCase() === "p") { e.preventDefault(); printDeck(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev, navigate]);

  // On mobile (and any time the slide changes), jump back to the top so the
  // viewer always starts reading a new slide from its headline instead of
  // landing mid-scroll where the previous slide left off.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [idx]);

  const Slide = SLIDES[idx].Comp;

  return (
    <div className="app-canvas min-h-screen w-full overflow-hidden relative pitch-root" data-testid="pitch-deck-page">
      {/* Ambient blobs */}
      <div className="pointer-events-none absolute inset-0 print:hidden">
        <div className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full" style={{ background: "radial-gradient(closest-side, rgba(34,211,238,0.18), transparent 70%)" }} />
        <div className="absolute -bottom-40 -right-40 h-[640px] w-[640px] rounded-full" style={{ background: "radial-gradient(closest-side, rgba(59,130,246,0.16), transparent 70%)" }} />
      </div>

      {/* Top bar (hidden when printing) */}
      <div className="absolute top-0 inset-x-0 z-30 px-5 sm:px-10 py-5 flex items-center justify-between print:hidden">
        <Link to="/login" data-testid="pitch-back-to-login" className="inline-flex items-center gap-2.5 group">
          <div className="h-8 w-8 rounded-lg flex items-center justify-center bg-gradient-to-br from-cyan-400 to-blue-500 shadow-[0_0_18px_rgba(34,211,238,0.4)]">
            <Hexagon className="h-4 w-4 text-[#04111A]" strokeWidth={2.5} />
          </div>
          <div className="text-1 font-semibold tracking-tight">OmniDraftLab</div>
          <span className="ml-2 text-3 text-[10px] uppercase tracking-[0.2em] hidden sm:inline">Pitch Deck</span>
        </Link>
        <div className="flex items-center gap-2">
          <button onClick={printDeck} data-testid="pitch-print" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-2 hover:text-1 hover:bg-white/5 transition text-xs font-medium" aria-label="Download PDF">
            <Printer className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Download PDF</span>
          </button>
          <button onClick={toggleFs} data-testid="pitch-fs-toggle" className="text-2 hover:text-1 p-2 rounded-lg hover:bg-white/5 transition" aria-label="Fullscreen">
            {fs ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </button>
          <Link to="/" data-testid="pitch-close" className="text-2 hover:text-1 p-2 rounded-lg hover:bg-white/5 transition" aria-label="Close">
            <X className="h-4 w-4" />
          </Link>
        </div>
      </div>

      {/* Stage — interactive view */}
      <div className="relative z-10 min-h-screen flex items-center justify-center px-6 sm:px-12 pt-20 pb-28 print:hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4, ease: [0.22, 0.65, 0.2, 1] }}
            className="w-full max-w-[1380px]"
            data-testid={`pitch-slide-${idx}`}
          >
            <Slide onBegin={next} />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Print-only stack — every slide on its own page */}
      <div className="hidden print:block">
        {SLIDES.map((s) => {
          const SlideComp = s.Comp;
          return (
            <section key={s.id} className="print-slide">
              <SlideComp />
            </section>
          );
        })}
      </div>

      {/* Footer controls */}
      <div className="absolute bottom-0 inset-x-0 z-30 px-5 sm:px-10 py-5 flex items-center justify-between gap-3 print:hidden">
        <button data-testid="pitch-prev" onClick={prev} disabled={idx === 0} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.07] text-2 disabled:opacity-30 transition">
          <ChevronLeft className="h-4 w-4" /> <span className="hidden sm:inline">Prev</span>
        </button>
        <div className="flex items-center gap-1.5 max-w-[60%] overflow-hidden" data-testid="pitch-progress">
          {SLIDES.map((_, i) => (
            <button key={i} onClick={() => setIdx(i)} data-testid={`pitch-dot-${i}`} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-cyan-300" : "w-1.5 bg-white/15 hover:bg-white/30"}`} aria-label={`Slide ${i + 1}`} />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-3 text-xs hidden sm:inline tabular-nums">{idx + 1} / {total}</span>
          <button data-testid="pitch-next" onClick={next} disabled={idx === total - 1} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-cyan-300 text-[#04111A] hover:bg-cyan-200 font-semibold disabled:opacity-30 transition">
            <span className="hidden sm:inline">Next</span> <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
